/**
 * Cuánto ocupa la hoja (bottom sheet) que está abierta, para que el
 * reproductor de "Escuchar" pueda apoyarse en su borde en vez de quedar tapado
 * (card #281, decisión del dueño: «que el reproductor acompañe a la tarjeta»).
 *
 * Quién publica: `useSheetDrag`, que es el embudo por el que pasan TODAS las
 * hojas de la app. Ninguna hoja tiene que enterarse de esto ni cablear nada —
 * y la número 24 tampoco.
 *
 * Quién consume: `GlobalListenBar`. La barra lee el alto y se sube a
 * `alto + aire`; cuando no hay hoja el alto es 0 y vuelve a su lugar de
 * siempre. Como es el MISMO elemento con una transición, el viaje se ve.
 *
 * Es un store de módulo (no un contexto) porque la hoja y la barra viven en
 * árboles distintos —hay hojas portaleadas a `<body>`— y un contexto no cruza
 * ese límite sin envolver a todo el mundo.
 *
 * El valor publicado es el alto VISIBLE: mientras arrastrás la hoja hacia
 * abajo, baja con ella (`useSheetDrag` descuenta el arrastre), así el
 * reproductor la acompaña también en el gesto de cierre.
 */
import { useSyncExternalStore } from 'react';

/** Una entrada por hoja montada: puede haber una hoja sobre otra. */
const heights = new Map<Element, number>();
const subscribers = new Set<() => void>();
let snapshot = 0;

function recompute(): void {
  // La barra se apoya en la hoja de más arriba: con dos abiertas, la más alta.
  let next = 0;
  for (const h of heights.values()) next = Math.max(next, h);
  const rounded = Math.round(next);
  if (rounded === snapshot) return;
  snapshot = rounded;
  for (const notify of subscribers) notify();
}

export function setSheetHeight(el: Element, height: number): void {
  heights.set(el, Math.max(0, height));
  recompute();
}

export function clearSheetHeight(el: Element): void {
  if (!heights.delete(el)) return;
  recompute();
}

function subscribe(onStoreChange: () => void): () => void {
  subscribers.add(onStoreChange);
  return () => {
    subscribers.delete(onStoreChange);
  };
}

/** Alto de la hoja abierta en px; `0` si no hay ninguna. */
export function useOpenSheetHeight(): number {
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => 0, // SSR / primer render del servidor: nunca hay hoja abierta
  );
}

/** Sólo para tests: deja el store como recién arrancado. */
export function resetSheetLift(): void {
  heights.clear();
  snapshot = 0;
  for (const notify of subscribers) notify();
}
